import {
  Grid,
  Box,
  Typography,
  FormControl,
  FormHelperText,
  TableBody,
  TableRow,
  TableHead,
  Table,
  TableContainer,
  TablePagination,
  SvgIcon,
  LinearProgress,
  Chip,
  Tooltip,
} from "@mui/material";
import React, { useEffect } from "react";
import { StyledPaper } from "../../../customComponents/styled/styledPaper";
import { useFormik } from "formik";
import * as yup from "yup";
import { StyledTextField } from "../../../customComponents/styled/styledInputs";
import { StyledLoadingButton } from "../../../customComponents/styled/styledButtons";
import {
  StyledTableCell,
  StyledTableRow,
} from "../../../customComponents/styled/styledTable";
import {
  useGetSessionsPaginatedQuery,
  useGetSessionQuery,
  useGetCurrentTermQuery,
  useCreateSessionMutation,
  useCreateTermMutation,
  useSetSessionOverrideMutation,
  useClearSessionOverrideMutation,
  useSetTermOverrideMutation,
  useClearTermOverrideMutation,
} from "./sessionApiSlice";
import { ReactComponent as EditIcon } from "../../../../assets/icons/edit.svg";
import EditSessionForm from "./EditSessionForm";
import EditTermForm from "./EditTermForm";
import RecordingDeadline from "./RecordingDeadline";
import { format, parseISO } from "date-fns";
import toast from "../../../../helpers/toast";

const validationSchema = yup.object({
  name: yup.string().required("Session name is required"),
  starts_at: yup.string().required("Required"),
  ends_at: yup.string().required("Required"),
});

const addTermValidation = yup.object({
  session_name: yup.string().required("current session required"),
  name: yup.string().required("Term name is required"),
  starts_at: yup.string().required("Required"),
  ends_at: yup.string().required("Required"),
  open_days: yup
    .number()
    .transform((value, originalValue) =>
      originalValue === "" || originalValue === null ? null : value
    )
    .nullable()
    .integer("Open Days must be a whole number")
    .min(0, "Open Days cannot be negative"),
});

function Session() {
  const [page, setPage] = React.useState(0);
  const PER_PAGE = 5;

  const { data: sessionsData, isLoading: fetchingSessions } =
    useGetSessionsPaginatedQuery({ page: page + 1, per_page: PER_PAGE });
  const sessions = sessionsData?.sessions ?? [];
  const totalSessions = sessionsData?.pagination?.total_items ?? 0;

  const { data: currentSession } = useGetSessionQuery("current");
  const { data: currentTerm } = useGetCurrentTermQuery();
  const [createSession, { isLoading: creatingSession }] =
    useCreateSessionMutation();

  const [createTerm, { isLoading: creatingTerm }] = useCreateTermMutation();

  const [setSessionOverride, { isLoading: settingSessionOverride }] = useSetSessionOverrideMutation();
  const [clearSessionOverride, { isLoading: clearingSessionOverride }] = useClearSessionOverrideMutation();
  const [setTermOverride, { isLoading: settingTermOverride }] = useSetTermOverrideMutation();
  const [clearTermOverride, { isLoading: clearingTermOverride }] = useClearTermOverrideMutation();

  // Detect whether the current session/term was forced (dates don't contain today).
  const now = new Date();
  const sessionIsForced = currentSession && (
    now < new Date(currentSession.starts_at) || now > new Date(currentSession.ends_at)
  );
  const termIsForced = currentTerm && (
    now < new Date(currentTerm.starts_at) || now > new Date(currentTerm.ends_at)
  );

  const handleSetSessionOverride = async (sessionId) => {
    try {
      await setSessionOverride({ session_id: sessionId }).unwrap();
      toast.success("Session override set");
    } catch (e) {
      toast.error(e?.data?.error || "Failed to set override");
    }
  };

  const handleClearSessionOverride = async () => {
    try {
      await clearSessionOverride().unwrap();
      toast.success("Session override cleared");
    } catch (e) {
      toast.error(e?.data?.error || "Failed to clear override");
    }
  };

  const handleSetTermOverride = async (termId) => {
    try {
      await setTermOverride({ term_id: termId }).unwrap();
      toast.success("Term override set");
    } catch (e) {
      toast.error(e?.data?.error || "Failed to set override");
    }
  };

  const handleClearTermOverride = async () => {
    try {
      await clearTermOverride().unwrap();
      toast.success("Term override cleared");
    } catch (e) {
      toast.error(e?.data?.error || "Failed to clear override");
    }
  };

  const [selectedSession, setSelectedSession] = React.useState(null);
  const [sessionToEdit, setSessionToEdit] = React.useState(null);
  const [termToEdit, setTermToEdit] = React.useState(null);

  useEffect(() => {
    if (selectedSession) {
      const updatedSession = sessions.find(
        (session) => session.id === selectedSession.id
      );
      if (updatedSession) setSelectedSession(updatedSession);
    }
  }, [sessions]);

  useEffect(() => {
    setSelectedSession(currentSession);
  }, [currentSession]);

  const formik = useFormik({
    initialValues: {
      name: "",
      starts_at: "",
      ends_at: "",
    },
    enableReinitialize: true,
    validationSchema: validationSchema,
    onSubmit: async (values) => {
      try {
        const response = await createSession(values).unwrap();
        if (response) {
          toast.success("Session Created Successfully");
          formik.resetForm();
        } else {
          toast.error("Error creating session ");
        }
      } catch (error) {
        toast.error(error?.message || "Something went wrong");
      }
    },
  });

  const addTermForm = useFormik({
    initialValues: {
      session_id: currentSession?.id || "",
      session_name: currentSession?.name || "",
      name: "",
      starts_at: "",
      ends_at: "",
      open_days: "",
    },
    enableReinitialize: true,
    validationSchema: addTermValidation,
    onSubmit: async (values) => {
      try {
        const payload = {
          ...values,
          open_days:
            values.open_days === "" || values.open_days === null
              ? null
              : Number(values.open_days),
        };
        const response = await createTerm(payload).unwrap();
        if (response) {
          toast.success("Term Created Successfully");
          addTermForm.resetForm();
        } else {
          toast.error("Error creating term");
        }
      } catch (error) {
        toast.error(error?.message || "Something went wrong");
      }
    },
  });

  return (
    <>
      <Grid container columnSpacing={3}>
        <Grid item xs={12} md={6}>
          <StyledPaper>
            {sessionToEdit ? (
              <EditSessionForm
                sessionToEdit={sessionToEdit}
                setSessionToEdit={setSessionToEdit}
              />
            ) : (
              <>
                <Box className="paperHeader">
                  <Typography variant="formHeading">Create Session</Typography>
                </Box>
                <Box className="paperBody">
                  <form onSubmit={formik.handleSubmit}>
                    <Grid container columnSpacing={1} rowSpacing={2}>
                      <Grid item xs={12}>
                        <FormControl error fullWidth>
                          <Typography variant="formSubHeading">
                            Session Name
                          </Typography>
                          <StyledTextField
                            autoFocus
                            variant="outlined"
                            id="name"
                            name="name"
                            value={formik.values.name}
                            onChange={formik.handleChange}
                            error={
                              formik.touched.name && Boolean(formik.errors.name)
                            }
                          />
                          <FormHelperText variant="error">
                            {formik.touched.name && formik.errors.name}
                          </FormHelperText>
                        </FormControl>
                      </Grid>

                      {/* End of first row */}

                      {/* End of second row */}

                      <Grid item xs={12} lg={6}>
                        <FormControl error fullWidth>
                          <Typography variant="formSubHeading">
                            Start date
                          </Typography>
                          <StyledTextField
                            type="date"
                            variant="outlined"
                            id="starts_at"
                            name="starts_at"
                            value={formik.values.starts_at}
                            onChange={formik.handleChange}
                            error={
                              formik.touched.starts_at &&
                              Boolean(formik.errors.starts_at)
                            }
                          />
                          <FormHelperText variant="error">
                            {formik.touched.starts_at &&
                              formik.errors.starts_at}
                          </FormHelperText>
                        </FormControl>
                      </Grid>
                      {/* End of third row */}

                      <Grid item xs={12} lg={6}>
                        <FormControl error fullWidth>
                          <Typography variant="formSubHeading">
                            End Date
                          </Typography>
                          <StyledTextField
                            type="date"
                            variant="outlined"
                            id="ends_at"
                            name="ends_at"
                            value={formik.values.ends_at}
                            onChange={formik.handleChange}
                            error={
                              formik.touched.ends_at &&
                              Boolean(formik.errors.ends_at)
                            }
                          />
                          <FormHelperText variant="error">
                            {formik.touched.ends_at && formik.errors.ends_at}
                          </FormHelperText>
                        </FormControl>
                      </Grid>

                      <Grid item xs={12} textAlign={"right"}>
                        <StyledLoadingButton
                          loading={creatingSession}
                          type="submit"
                        >
                          submit
                        </StyledLoadingButton>
                      </Grid>
                    </Grid>
                  </form>
                </Box>
              </>
            )}
          </StyledPaper>

          <StyledPaper>
            {termToEdit ? (
              <EditTermForm
                termToEdit={termToEdit}
                setTermToEdit={setTermToEdit}
              />
            ) : (
              <>
                <Box className="paperHeader">
                  <Typography variant="formHeading">Add Term</Typography>
                </Box>
                <Box className="paperBody">
                  <form onSubmit={addTermForm.handleSubmit}>
                    <Grid container columnSpacing={1} rowSpacing={2}>
                      <Grid item xs={12} lg={6}>
                        <FormControl error fullWidth>
                          <Typography variant="formSubHeading">
                            Session Name
                          </Typography>
                          <StyledTextField
                            disabled
                            variant="outlined"
                            id="session_name"
                            session_name="session_name"
                            value={addTermForm.values.session_name}
                            onChange={addTermForm.handleChange}
                            error={
                              addTermForm.touched.session_name &&
                              Boolean(addTermForm.errors.session_name)
                            }
                          />
                          <FormHelperText variant="error">
                            {addTermForm.touched.session_name &&
                              addTermForm.errors.session_name}
                          </FormHelperText>
                        </FormControl>
                      </Grid>

                      <Grid item xs={12} lg={6}>
                        <FormControl error fullWidth>
                          <Typography variant="formSubHeading">
                            Term Name
                          </Typography>
                          <StyledTextField
                            variant="outlined"
                            id="name"
                            name="name"
                            value={addTermForm.values.name}
                            onChange={addTermForm.handleChange}
                            error={
                              addTermForm.touched.name &&
                              Boolean(addTermForm.errors.name)
                            }
                          />
                          <FormHelperText variant="error">
                            {addTermForm.touched.name &&
                              addTermForm.errors.name}
                          </FormHelperText>
                        </FormControl>
                      </Grid>

                      {/* End of first row */}

                      {/* End of second row */}

                      <Grid item xs={12} lg={6}>
                        <FormControl error fullWidth>
                          <Typography variant="formSubHeading">
                            Start date
                          </Typography>
                          <StyledTextField
                            type="date"
                            variant="outlined"
                            id="starts_at"
                            name="starts_at"
                            value={addTermForm.values.starts_at}
                            onChange={addTermForm.handleChange}
                            error={
                              addTermForm.touched.starts_at &&
                              Boolean(addTermForm.errors.starts_at)
                            }
                          />
                          <FormHelperText variant="error">
                            {addTermForm.touched.starts_at &&
                              addTermForm.errors.starts_at}
                          </FormHelperText>
                        </FormControl>
                      </Grid>
                      {/* End of third row */}

                      <Grid item xs={12} lg={6}>
                        <FormControl error fullWidth>
                          <Typography variant="formSubHeading">
                            End Date
                          </Typography>
                          <StyledTextField
                            type="date"
                            variant="outlined"
                            id="ends_at"
                            name="ends_at"
                            value={addTermForm.values.ends_at}
                            onChange={addTermForm.handleChange}
                            error={
                              addTermForm.touched.ends_at &&
                              Boolean(addTermForm.errors.ends_at)
                            }
                          />
                          <FormHelperText variant="error">
                            {addTermForm.touched.ends_at &&
                              addTermForm.errors.ends_at}
                          </FormHelperText>
                        </FormControl>
                      </Grid>

                      <Grid item xs={12} lg={6}>
                        <FormControl error fullWidth>
                          <Typography variant="formSubHeading">
                            Open Days (Optional)
                          </Typography>
                          <StyledTextField
                            type="number"
                            variant="outlined"
                            id="open_days"
                            name="open_days"
                            value={addTermForm.values.open_days}
                            onChange={addTermForm.handleChange}
                            error={
                              addTermForm.touched.open_days &&
                              Boolean(addTermForm.errors.open_days)
                            }
                            inputProps={{ min: 0 }}
                          />
                          <FormHelperText variant="error">
                            {addTermForm.touched.open_days &&
                              addTermForm.errors.open_days}
                          </FormHelperText>
                        </FormControl>
                      </Grid>

                      <Grid item xs={12} textAlign={"right"}>
                        <StyledLoadingButton
                          loading={creatingTerm}
                          type="submit"
                        >
                          submit
                        </StyledLoadingButton>
                      </Grid>
                    </Grid>
                  </form>
                </Box>
              </>
            )}
          </StyledPaper>
        </Grid>

        {/* right pane  */}
        <Grid item xs={12} md={6}>
          <StyledPaper>
            {fetchingSessions && <LinearProgress />}
            <Box className="paperHeader">
              <Typography variant="formHeading">Sessions</Typography>
            </Box>
            <TableContainer sx={{ overflowX: "auto" }}>
              <Table sx={{ width: "100%", boxShadow: "none" }}>
                <TableHead>
                  <TableRow>
                    <StyledTableCell>session </StyledTableCell>
                    <StyledTableCell>start </StyledTableCell>
                    <StyledTableCell>end </StyledTableCell>
                    <StyledTableCell>edit </StyledTableCell>
                    <StyledTableCell>override </StyledTableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {sessions?.map((session) => (
                    <StyledTableRow key={session.id}>
                      <StyledTableCell
                        component="th"
                        scope="row"
                        onClick={() => setSelectedSession(session)}
                      >
                        <Box className="tableCellLink">
                          {session.name}
                          {session.id === currentSession?.id && (
                            <Chip
                              label={sessionIsForced ? "overridden" : "current"}
                              size="small"
                              color={sessionIsForced ? "warning" : "success"}
                              sx={{ ml: 1, height: 18, fontSize: "0.65rem" }}
                            />
                          )}
                        </Box>
                      </StyledTableCell>
                      <StyledTableCell component="th" scope="row">
                        {format(parseISO(session.starts_at), "dd-MM-yyyy")}
                      </StyledTableCell>
                      <StyledTableCell component="th" scope="row">
                        {format(parseISO(session.ends_at), "dd-MM-yyyy")}
                      </StyledTableCell>
                      <StyledTableCell>
                        <SvgIcon
                          sx={{ color: "primary.safeOnLight", cursor: "pointer" }}
                          component={EditIcon}
                          inheritViewBox
                          onClick={() => setSessionToEdit(session)}
                        />
                      </StyledTableCell>
                      <StyledTableCell>
                        {session.id === currentSession?.id && sessionIsForced ? (
                          <Tooltip title="Resume automatic session detection">
                            <Chip
                              label="Clear"
                              size="small"
                              color="warning"
                              variant="outlined"
                              onClick={handleClearSessionOverride}
                              disabled={clearingSessionOverride}
                              sx={{ cursor: "pointer" }}
                            />
                          </Tooltip>
                        ) : session.id !== currentSession?.id ? (
                          <Tooltip title="Force this session as active">
                            <Chip
                              label="Set active"
                              size="small"
                              variant="outlined"
                              onClick={() => handleSetSessionOverride(session.id)}
                              disabled={settingSessionOverride}
                              sx={{ cursor: "pointer" }}
                            />
                          </Tooltip>
                        ) : null}
                      </StyledTableCell>
                    </StyledTableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
            {totalSessions > PER_PAGE && (
              <TablePagination
                component="div"
                count={totalSessions}
                rowsPerPage={PER_PAGE}
                rowsPerPageOptions={[PER_PAGE]}
                page={page}
                onPageChange={(_, newPage) => setPage(newPage)}
              />
            )}
          </StyledPaper>

          <StyledPaper>
            <Box className="paperHeader">
              <Typography variant="formHeading">
                {selectedSession?.name} Terms
              </Typography>
            </Box>
            <TableContainer sx={{ pb: 4 }}>
              <Table sx={{ width: "100%", boxShadow: "none" }}>
                <TableHead>
                  <TableRow>
                    <StyledTableCell>term </StyledTableCell>
                    <StyledTableCell>start </StyledTableCell>
                    <StyledTableCell>end </StyledTableCell>
                    <StyledTableCell align="left">edit</StyledTableCell>
                    <StyledTableCell align="left">override</StyledTableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {selectedSession?.terms?.map((row, i) => (
                    <StyledTableRow key={i}>
                      <StyledTableCell component="th" scope="row">
                        {row.name}
                        {row.id === currentTerm?.id && (
                          <Chip
                            label={termIsForced ? "overridden" : "current"}
                            size="small"
                            color={termIsForced ? "warning" : "success"}
                            sx={{ ml: 1, height: 18, fontSize: "0.65rem" }}
                          />
                        )}
                      </StyledTableCell>
                      <StyledTableCell component="th" scope="row">
                        {format(parseISO(row.starts_at), "dd-MM-yyyy")}
                      </StyledTableCell>
                      <StyledTableCell component="th" scope="row">
                        {format(parseISO(row.ends_at), "dd-MM-yyyy")}
                      </StyledTableCell>
                      <StyledTableCell align="left">
                        <SvgIcon
                          sx={{ color: "primary.safeOnLight", cursor: "pointer" }}
                          component={EditIcon}
                          inheritViewBox
                          onClick={() => setTermToEdit(row)}
                        />
                      </StyledTableCell>
                      <StyledTableCell align="left">
                        {row.id === currentTerm?.id && termIsForced ? (
                          <Tooltip title="Resume automatic term detection">
                            <Chip
                              label="Clear"
                              size="small"
                              color="warning"
                              variant="outlined"
                              onClick={handleClearTermOverride}
                              disabled={clearingTermOverride}
                              sx={{ cursor: "pointer" }}
                            />
                          </Tooltip>
                        ) : row.id !== currentTerm?.id ? (
                          <Tooltip title="Force this term as active">
                            <Chip
                              label="Set active"
                              size="small"
                              variant="outlined"
                              onClick={() => handleSetTermOverride(row.id)}
                              disabled={settingTermOverride}
                              sx={{ cursor: "pointer" }}
                            />
                          </Tooltip>
                        ) : null}
                      </StyledTableCell>
                    </StyledTableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          </StyledPaper>
        </Grid>
      </Grid>

      <RecordingDeadline />
    </>
  );
}

export default Session;
